import type { ParsedDictationChunk } from '../createNvoqWebsocketFactory';

/**
 * Checks if a dictation chunk is a text type
 */
export const isText = (item: ParsedDictationChunk): boolean =>
  item.type === 'TEXT' || item.type === 'stable_text';

/**
 * Consolidated text extraction utility - used by multiple functions
 * Extracts and normalizes text from a dictation chunk
 */
export const extractTextFromItem = (item: ParsedDictationChunk): string | null => {
  if (!isText(item)) {
    return null;
  }

  // Handle NvoqMessage format (has data.text)
  if ((item.payload as any).data?.text) {
    return (item.payload as any).data.text.toLowerCase().trim();
  }

  // Handle StableTextResponse format (has payload.text)
  if ((item.payload as any).text) {
    return (item.payload as any).text.toLowerCase().trim();
  }

  return null;
};

/**
 * Consolidated last word extraction utility
 * Extracts the last word from a text string
 */
export const getLastWord = (text: string): string | null => {
  const words = text.split(/\s+/);
  const lastWord = words.pop();
  return lastWord || null;
};

/**
 * Optimized function factory for text ending checks
 * Creates a function that checks if the last word of a dictation chunk matches a condition
 */
export const createTextEndsChecker = (checkFn: (lastWord: string) => boolean) => {
  return (item: ParsedDictationChunk): boolean => {
    const text = extractTextFromItem(item);
    if (!text) return false;

    const lastWord = getLastWord(text);
    if (!lastWord) return false;

    return checkFn(lastWord);
  };
};

/**
 * Optimized function factory for text contains checks
 * Creates a function that checks if the entire text of a dictation chunk matches a condition
 */
export const createTextContainsChecker = (checkFn: (text: string) => boolean) => {
  return (item: ParsedDictationChunk): boolean => {
    const text = extractTextFromItem(item);
    if (!text) return false;

    return checkFn(text);
  };
};

/**
 * Checks if a text string ends with sentence ending punctuation
 */
export const maybeEndOfSentence = (text: string): boolean => {
  return /[.!?]\s*$/.test(text);
};
